import { useState, useMemo } from "react";
import { Button as AntButton, message, Modal, Space, Spin, Form, Input, InputNumber, Select, DatePicker } from "antd";
import { PlusOutlined, StopOutlined, CheckCircleOutlined, SearchOutlined, DownloadOutlined, MailOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  fetchSales,
  fetchSalesStats,
  fetchSellerListings,
  fetchDealerPaymentAccounts,
  createSale,
  voidSale,
  downloadInvoicePdf,
  sendInvoiceEmail,
  type SaleRow,
} from "../api/auth";
import { useAuth } from "../auth/AuthContext";
import { Metric } from "../components/Metric";
import { DataTable, Empty, ErrorNote, PageHeader, PageShell, Panel, StatusTag } from "../components/Page";

const { Option } = Select;

const bdt = (n: number) => `৳ ${Number(n || 0).toLocaleString("en-IN")}`;

export default function SalesPage() {
  const { seller } = useAuth();
  const queryClient = useQueryClient();
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string | undefined>(undefined);
  const [recordModalOpen, setRecordModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();
  const filters = useMemo(
    () => ({ status: statusFilter || undefined, paymentStatus: paymentStatusFilter || undefined, q: search || undefined }),
    [statusFilter, paymentStatusFilter, search],
  );

  const {
    data: salesData,
    isPending,
    error,
    refetch,
  } = useQuery({
    queryKey: ["sales-list", page, PAGE_SIZE, JSON.stringify(filters)],
    queryFn: () => fetchSales(page, PAGE_SIZE, filters),
  });

  const { data: stats } = useQuery({
    queryKey: ["sales-stats"],
    queryFn: fetchSalesStats,
  });

  const { data: activeData } = useQuery({
    queryKey: ["active-listings-dropdown"],
    queryFn: () => fetchSellerListings(1, 100),
  });

  const { data: paymentAccounts = [] } = useQuery({
    queryKey: ["dealer-payment-accounts"],
    queryFn: () => fetchDealerPaymentAccounts(),
    enabled: !!seller,
  });

  const availableBikes = useMemo(
    () => (activeData?.rows ?? []).filter((r) => r.status === "PUBLISHED" || r.status === "PAUSED"),
    [activeData],
  );

  const handleBikeSelect = (listingId: string) => {
    const bike = availableBikes.find((b) => b.id === listingId);
    if (bike && bike.price) form.setFieldsValue({ bikePrice: bike.price });
  };

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["sales-list"] }),
      queryClient.invalidateQueries({ queryKey: ["sales-stats"] }),
      queryClient.invalidateQueries({ queryKey: ["active-listings-dropdown"] }),
      queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      queryClient.invalidateQueries({ queryKey: ["dealer-payment-accounts"] }),
      queryClient.invalidateQueries({ queryKey: ["dealer-ledger"] }),
      queryClient.invalidateQueries({ queryKey: ["dealer-ledger-stats"] }),
    ]);
  };

  const handleRecordSubmit = async (values: {
    listingId: string;
    bikePrice: number;
    customerName: string;
    customerPhone: string;
    paymentAccountId?: string;
    firstPaymentAmount?: number;
    transactionReference?: string;
    saleDate?: dayjs.Dayjs;
    notes?: string;
  }) => {
    setIsSubmitting(true);
    try {
      await createSale({
        listingId: values.listingId,
        bikePrice: values.bikePrice,
        customerName: values.customerName.trim(),
        customerPhone: values.customerPhone.trim(),
        paymentAccountId: values.paymentAccountId,
        firstPaymentAmount: values.firstPaymentAmount,
        transactionReference: values.transactionReference?.trim(),
        saleDate: values.saleDate ? values.saleDate.toISOString() : undefined,
        notes: values.notes?.trim(),
      });
      message.success("Sale recorded and invoice generated.");
      setRecordModalOpen(false);
      form.resetFields();
      await invalidateAll();
      setPage(1);
      refetch();
    } catch (err: unknown) {
      message.error((err as { message?: string })?.message ?? "Failed to record sale.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVoid = (record: SaleRow) => {
    Modal.confirm({
      title: "Void this sale?",
      content: `Invoice ${record.invoiceNo} will be voided, any payments reversed, and the bike returned to inventory. This is recorded, never deleted.`,
      okText: "Void Sale",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await voidSale(record.id, "Voided from the sale book");
          message.success("Sale voided.");
          await invalidateAll();
          refetch();
        } catch (err: unknown) {
          message.error((err as { message?: string })?.message ?? "Failed to void sale.");
        }
      },
    });
  };

  const handleDownloadInvoice = async (record: SaleRow) => {
    try {
      const blob = await downloadInvoicePdf(record.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${record.invoiceNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      message.error((err as { message?: string })?.message ?? "Failed to download invoice");
    }
  };

  const handleEmailInvoice = async (record: SaleRow) => {
    try {
      await sendInvoiceEmail(record.id);
      message.success(`Invoice emailed to ${record.customer.email ?? "customer"}`);
    } catch (err) {
      message.error((err as { message?: string })?.message ?? "Failed to send invoice email");
    }
  };

  const rows = useMemo(() => salesData?.rows ?? [], [salesData]);

  const columns: ColumnsType<SaleRow> = [
    {
      title: "Invoice / Bike",
      render: (_text, record) => (
        <div className="min-w-0">
          <div className="font-medium text-ink">{record.listing?.title ?? "Vehicle"}</div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="font-mono text-[11px] text-ink-faint">{record.invoiceNo}</span>
            {record.listing?.publicId && (
              <span className="font-mono text-[11px] text-ink-faint">{record.listing.publicId}</span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Buyer",
      render: (_text, record) => (
        <div>
          <div className="font-medium text-ink">{record.customer.name}</div>
          <div className="text-xs text-ink-muted">{record.customer.phone}</div>
        </div>
      ),
    },
    { title: "Total", width: 130, render: (_t, r) => <span className="tnum">{bdt(r.totalAmount)}</span> },
    { title: "Paid", width: 120, render: (_t, r) => <span className="tnum">{bdt(r.paidAmount)}</span> },
    {
      title: "Due",
      width: 120,
      render: (_t, r) => <span className={`tnum ${r.dueAmount > 0 ? "text-amber-700" : ""}`}>{bdt(r.dueAmount)}</span>,
    },
    { title: "Payment", width: 110, render: (_t, r) => <StatusTag status={r.paymentStatus} /> },
    { title: "Sale", width: 100, render: (_t, r) => <StatusTag status={r.status} /> },
    {
      title: "Date",
      width: 120,
      render: (_t, r) => (
        <span className="tnum text-xs text-ink-muted">
          {r.saleDate ? new Date(r.saleDate).toLocaleDateString() : "—"}
        </span>
      ),
    },
    {
      title: "Actions",
      width: 250,
      render: (_text, record) => (
        <Space size="small">
          <AntButton size="small" icon={<DownloadOutlined />} onClick={() => handleDownloadInvoice(record)} title="Download invoice PDF">
            Invoice
          </AntButton>
          <AntButton size="small" icon={<MailOutlined />} onClick={() => handleEmailInvoice(record)} title="Email invoice to buyer">
            Email
          </AntButton>
          <AntButton size="small" danger icon={<StopOutlined />} onClick={() => handleVoid(record)} title="Void sale">
            Void
          </AntButton>
        </Space>
      ),
    },
  ];

  if (error) {
    return (
      <PageShell>
        <ErrorNote error={error} />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        eyebrow="Transactions"
        title="Sale Management"
        description="Every bike sold, with its invoice, payments and outstanding balance."
        actions={
          <AntButton
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              const defaultAcc = paymentAccounts.find((a) => a.isDefault) || paymentAccounts[0];
              form.setFieldsValue({ saleDate: dayjs(), paymentAccountId: defaultAcc?.id });
              setRecordModalOpen(true);
            }}
          >
            Record Sale
          </AntButton>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 mb-6">
        <Metric label="Sales" value={stats?.count ?? 0} coverage="live" hint="Non-void sales on record" />
        <Metric label="Revenue" value={bdt(stats?.totalRevenue ?? 0)} coverage="live" hint="Total invoiced value" />
        <Metric label="Collected" value={bdt(stats?.totalCollected ?? 0)} coverage="live" hint="Payments received" />
        <Metric label="Outstanding" value={bdt(stats?.totalDue ?? 0)} coverage="live" hint="Balance still due" />
      </div>

      <Panel
        flush
        title="Sales Records"
        description="Completed sales, their invoices and balances."
        action={
          <div className="flex items-center gap-3">
            <select
              className="w-40"
              onChange={(e) => setStatusFilter(e.target.value as string)}
              value={statusFilter || ""}
            >
              <option value="">All statuses</option>
              <option value="VOID">Void</option>
              <option value="PUBLISHED">Published</option>
              <option value="PAUSED">Paused</option>
            </select>
            <select
              className="w-40"
              onChange={(e) => setPaymentStatusFilter(e.target.value as string)}
              value={paymentStatusFilter || ""}
            >
              <option value="">All payment statuses</option>
              <option value="PAID">Paid</option>
              <option value="PENDING">Pending</option>
              <option value="OVERDUE">Overdue</option>
            </select>
            <Input
              placeholder="Search invoice, buyer or bike…"
              prefix={<SearchOutlined className="text-ink-faint" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-56"
              allowClear
              size="small"
            />
          </div>
        }
      >
        {isPending ? (
          <div className="flex justify-center py-16">
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <Empty title="No sales recorded yet">
            When you complete a sale, click &ldquo;Record Sale&rdquo; to create the sale, its invoice and its ledger entry.
          </Empty>
        ) : (
          <DataTable<SaleRow>
            columns={columns}
            rows={rows}
            total={salesData?.total}
            page={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        )}
      </Panel>

      <Modal
        title="Record Completed Sale"
        open={recordModalOpen}
        onCancel={() => setRecordModalOpen(false)}
        footer={null}
        destroyOnClose
        centered
        width={560}
      >
        <p className="mb-4 text-xs text-ink-muted">
          Creates a sale record, an invoice and a ledger entry in one step. The bike is marked sold.
        </p>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleRecordSubmit}
          initialValues={{ saleDate: dayjs() }}
        >
          <Form.Item
            name="listingId"
            label="Select Bike"
            rules={[{ required: true, message: "Please select a bike to mark as sold" }]}
          >
            <Select
              placeholder="Search from published/paused inventory…"
              showSearch
              optionFilterProp="children"
              onChange={handleBikeSelect}
              filterOption={(input, option) =>
                (option?.children as unknown as string ?? "").toLowerCase().includes(input.toLowerCase())
              }
            >
              {availableBikes.map((bike) => (
                <Option key={bike.id} value={bike.id}>
                  {bike.title} ({bike.publicId}) {bike.price ? `— ৳ ${Number(bike.price).toLocaleString()}` : ""}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Form.Item name="bikePrice" label="Final Sale Price (BDT)" rules={[{ required: true, message: "Sale price is required" }]}>
              <InputNumber className="w-full" min={0} prefix="৳" placeholder="Agreed price in BDT" />
            </Form.Item>
            <Form.Item name="firstPaymentAmount" label="First Payment (optional)">
              <InputNumber className="w-full" min={0} prefix="৳" placeholder="Amount received now" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Form.Item name="customerName" label="Buyer Full Name" rules={[{ required: true, message: "Buyer name is required" }]}>
              <Input placeholder="e.g. Tanvir Rahman" />
            </Form.Item>
            <Form.Item name="customerPhone" label="Buyer Contact Phone" rules={[{ required: true, message: "Buyer phone is required" }]}>
              <Input placeholder="017xxxxxxxx" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Form.Item name="paymentAccountId" label="Receiving Account" extra="Used if a first payment is entered.">
              <Select placeholder="Default cash account" showSearch allowClear>
                {paymentAccounts.map((acc) => (
                  <Option key={acc.id} value={acc.id}>
                    {acc.name} — ৳{Number(acc.currentBalance).toLocaleString()}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="transactionReference" label="Trx / Voucher Reference">
              <Input placeholder="e.g. TrxID / Cheque # / Voucher #" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Form.Item name="saleDate" label="Transaction Date">
              <DatePicker className="w-full" format="YYYY-MM-DD" />
            </Form.Item>
            <Form.Item name="notes" label="Notes / Remarks">
              <Input placeholder="Invoice reference or receipt notes" />
            </Form.Item>
          </div>

          <div className="mt-6 flex justify-end gap-2 border-t border-line pt-4">
            <AntButton onClick={() => setRecordModalOpen(false)}>Cancel</AntButton>
            <AntButton type="primary" htmlType="submit" loading={isSubmitting} icon={<CheckCircleOutlined />}>
              Confirm & Save Sale
            </AntButton>
          </div>
        </Form>
      </Modal>
    </PageShell>
  );
}